import { FC } from 'react';
import { Claim } from '@scale/shared/types/queue';
import { Tag } from '@scale/scaleui';
import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import moment from 'moment';
import dateDiff from '@scale/shared/timeUtils';

type InfoTaskExpirationProps = {
  claim?: Claim;
  className?: string;
};

export function timeRemaining(until: Date, now: Date) {
  const untilDate = moment(until).utc();
  const nowDate = moment(now).utc();
  const maxClaimedDays = untilDate.diff(nowDate, 'days');
  const maxClaimedHours = untilDate.diff(nowDate, 'hours') % 24;
  const maxClaimedMinutes = untilDate.diff(nowDate, 'minutes') % 60;

  const wordMinutes = maxClaimedMinutes > 1 ? 'minutes' : 'minute';
  const days = maxClaimedDays > 0 ? `${maxClaimedDays} days, ` : '';
  const hours = maxClaimedHours > 0 ? `${maxClaimedHours} hours, ` : '';
  const minutes = maxClaimedMinutes > 0 ? `${maxClaimedMinutes} ${wordMinutes}` : '';
  return { days, hours, minutes };
}

function differenceBetweenDatesToString(until: Date, now: Date) {
  const untilDate = moment(until).utc();
  const nowDate = moment(now).utc();
  const prefix = untilDate.diff(nowDate) <= 0 ? 'Claim Expired' : 'Idle Time Remaining: ';
  const { days, hours, minutes } = timeRemaining(until, now);
  return `${prefix}${days}${hours}${minutes}`;
}

export const ChipTaskExpiration: FC<InfoTaskExpirationProps> = ({ claim, className }) => {
  const { claimedUntil, now, idleMinutes } = useClaimTime(claim);
  if (!claim) return null;

  const originalTaskDuration = idleMinutes;

  const claimedRemainingMinutes = dateDiff(claimedUntil, now, 'minutes') || 0;

  const greenTime: boolean = claimedRemainingMinutes >= 0.8 * originalTaskDuration;
  const yellowTime: boolean =
    claimedRemainingMinutes >= 0.2 * originalTaskDuration &&
    claimedRemainingMinutes < 0.8 * originalTaskDuration;

  const maxClaimedForStr = differenceBetweenDatesToString(claimedUntil, now);

  return (
    <Tag
      title="Task Expires"
      icon="circle"
      variant={greenTime ? 'success' : yellowTime ? 'warning' : 'danger'}
      className={className}
    >
      {maxClaimedForStr}
    </Tag>
  );
};
